import React, {
  CSSProperties,
  PropsWithChildren,
  ReactElement,
} from "react";
import styles from "../data-table.module.scss";

import {
  CommercialTypeTag,
  GovernmentalTypeTag,
  IndustrialTypeTag,
  ResidentialTypeTag,
  ServiceTypeTag,
} from "../../tags/type-of-use-tag";
import Checkbox from "../../checkbox/checkbox";

interface TableRowProps extends PropsWithChildren {
  id: number;
  data: Record<string, any>;
  dateTemplate?: string;
  style?: CSSProperties;
  selected?: number[];
  addSelected?: (ids: number[]) => void;
  deleteSelected?: (ids: number[]) => void;
  renderRowMenu?: (rowData: Record<string, any>) => React.ReactNode;
  secondLineOpacity?: boolean;
  visibleSelect?: boolean;
  onRowClick?: (rowData: Record<string, any>) => void;
  getRowColor?: (rowData: Record<string, any>) => string | undefined;
}

export default function TableRow({
  id,
  data,
  style,
  children,
  selected,
  deleteSelected,
  addSelected,
  renderRowMenu,
  secondLineOpacity,
  visibleSelect,
  onRowClick,
  getRowColor,
}: TableRowProps): ReactElement {

  const isChecked = selected?.includes(id) ?? false;

  const handleCheckboxChange = () => {
    if (!visibleSelect) return;
    if (isChecked) {
      deleteSelected?.([id]);
    } else {
      addSelected?.([id]);
    }
  };

  function normalizeCaption(caption: string) {
    return caption
        .split("\n")[0]
        .split("(")[0]
        .replace(/[^\w]/g, "");
  }

  const handleRowClick = () => {
    const selectionText = window.getSelection()?.toString();
    if (selectionText) {
      return;
    }
    onRowClick?.(data);
  };

  const rowBg = getRowColor?.(data)
  const rowStyle: CSSProperties = {
    ...style,
    ...(onRowClick ? { cursor: "pointer" } : {}),
    ...(rowBg ? {backgroundColor: rowBg} : {}),
  }

  return (
    <div
        style={rowStyle}
        className={styles.tr}
        onClick={handleRowClick}
    >
      {/* <tr style={style}> */}
      {visibleSelect && (
          <div style={{ width: "40px", marginRight: "0" }} className={styles.td}>
            {/* <td style={{width: "70px"}}> */}
            <Checkbox checked={isChecked} onChange={handleCheckboxChange} />
          </div>
      )}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          const field = child.props.dataField;
          const value = data[field];
          const renderCell = child.props.renderCell;
          let cellContent;
          if (renderCell) {
            cellContent = renderCell(value, data);
          } else if (field === "landUse") {
            if (!value) return;
            switch (value) {
              case "Commercial": {
                cellContent = <CommercialTypeTag />;
                break;
              }
              case "Governmental": {
                cellContent = <GovernmentalTypeTag />;
                break;
              }
              case "Industrial": {
                cellContent = <IndustrialTypeTag />;
                break;
              }
              case "Residential": {
                cellContent = <ResidentialTypeTag />;
                break;
              }
              case "Service": {
                cellContent = <ServiceTypeTag />;
                break;
              }
            }
          } else if (field == "status") {
            cellContent = (
              <span
                className={
                  styles[
                    value
                      .split(" ")
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase(),
                      )
                      .join("")
                  ]
                }
              >
                {value
                  .split(" ")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(" ")}
              </span>
            );
          } else if (
            value &&
            typeof value == "string" &&
            value.includes("\n")
          ) {
            const splited = value.split("\n");
            cellContent = (
              <>
                {splited[0]}
                <br />
                <span style={secondLineOpacity ? { opacity: 0.5 } : undefined}>
                  {splited[1]}
                </span>
              </>
            );
          } else {
            cellContent = value;
          }
          return (
            <div
              key={field}
              className={`${styles.td} ${child.props.caption ? styles[normalizeCaption(child.props.caption)] : ''}`}
              style={child.props.style}
            >
              {cellContent}
            </div>
          );
        }
        return null;
      })}
      {renderRowMenu && (
          <div className={styles.td}>
            {renderRowMenu(data)}
          </div>
      )}
    </div>
  );
}
